/**
 * Raid Sim Lab HUD — dual A/B 0..100 banks + deck transport (play/pause/stop/reset).
 * Mounted on full overlay (not SafeFrame) so letterbox margins hold controls.
 */
import { hasString, t } from "../../../data/strings";
import { raidCatalog, type RaidClassId } from "../../../data/raid-catalog";
import {
  escapeAttr,
  judgmentSellHelp,
  raidLeaderCore,
  type JudgmentTraitHelpId,
} from "../../../data/judgment-sell-help";
import {
  authoredStatsFloor,
  previewAiDerivedFromPrimaries,
} from "../../../features/raid";
import { listSimScenarios } from "../../../features/raid/sim-lab/sim-lab-runner";
import {
  simTransportButtons,
  type SimTransportPhase,
} from "../../../features/raid/sim-lab/sim-lab-transport";
import type {
  SimEditPatch,
  SimLaneBank,
  SimPrimaryId,
  SimReport,
  SimScenarioId,
} from "../../../features/raid/sim-lab/sim-lab-types";
import { formatDisplay, formatInt } from "../../../view/display-format";
import { getUiLayout } from "../../layout";

export type SimPlayMode = "single" | "compare";

export type SimPlayRequest = {
  scenarioId: SimScenarioId;
  patch: SimEditPatch;
};

/** Shared cue payload — single uses A; compare uses A|B. */
export type SimCueRequest = {
  mode: SimPlayMode;
  scenarioId: SimScenarioId;
  a: SimPlayRequest;
  b: SimPlayRequest;
};

export type RaidSimHudHandlers = {
  onToRoster: () => void;
  onBack: () => void;
  /** Setup board at scenario start; leave paused ready (shared A|B transport). */
  onCue: (req: SimCueRequest) => void;
  /** Start or resume shared clock. */
  onPlay: (req: SimCueRequest) => void;
  /** Freeze clock in place (no rewind). */
  onPause: () => void;
  /** Cue to scenario start with current banks; paused ready. */
  onStop: (req: SimCueRequest) => void;
  /** Reload scenario initials/pitch + cue; paused ready. */
  onReset: (req: SimCueRequest) => void;
};

export type RaidSimHudStatus = {
  phase: SimTransportPhase | "idle";
  tag?: string;
  elapsedSec?: number;
  activeId?: string | null;
  caption?: string;
};

export type RaidSimHudApi = {
  destroy: () => void;
  setTransportPhase: (phase: SimTransportPhase) => void;
  setStatus: (status: RaidSimHudStatus) => void;
  setOutcome: (args: {
    reports: readonly SimReport[];
    summary?: readonly string[];
  }) => void;
};

/**
 * Judgment Selling UI order: 직감 · 통찰 · 유대 · 인내(격하).
 * Runtime absolute still uses measure+selfProtect; insight slider writes both.
 */
const SLIDER_KEYS = ["instinct", "insight", "bond", "grit"] as const;
type SliderKey = (typeof SLIDER_KEYS)[number];

type SliderState = Record<SliderKey, number>;

/** Lab neutral — used when scenario has no pitch and focus axes default mid. */
const NEUTRAL_PRIMARIES: SliderState = {
  instinct: 50,
  insight: 50,
  bond: 50,
  grit: 50,
};

function clamp01_100(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function classIdFor(defId: string): RaidClassId {
  const row = raidCatalog.roster.pool.find((p) => p.id === defId) as
    | { id: string; classId: RaidClassId }
    | undefined;
  if (!row) throw new Error(`sim-lab: unknown focus ${defId}`);
  return row.classId;
}

/** Authored floor — scenario change / first mount defaults. */
function floorFor(defId: string): SliderState {
  const floor = authoredStatsFloor(defId, classIdFor(defId));
  const measure = clamp01_100(floor.measure ?? 50);
  const selfProtect = clamp01_100(floor.selfProtect ?? 50);
  return {
    instinct: clamp01_100(floor.instinct ?? 50),
    insight: clamp01_100(0.5 * measure + 0.5 * selfProtect),
    bond: clamp01_100(floor.bond ?? 50),
    grit: clamp01_100(floor.grit ?? 50),
  };
}

function copyPrimaries(s: SliderState): SliderState {
  return {
    instinct: clamp01_100(s.instinct),
    insight: clamp01_100(s.insight),
    bond: clamp01_100(s.bond),
    grit: clamp01_100(s.grit),
  };
}

/** Sim Lab forces absolute 0..100 (see RaidAiSave.absolute). */
function patchFromAbsolute(focusDefId: string, s: SliderState): SimEditPatch {
  const insight = clamp01_100(s.insight);
  return {
    absolute: {
      [focusDefId]: {
        grit: s.grit,
        measure: insight,
        selfProtect: insight,
        bond: s.bond,
        instinct: s.instinct,
      },
    },
  };
}

/** Legacy bank keys (measure/selfProtect) → sell-sheet insight. */
function bankToSlider(floor: SliderState, lane: SimLaneBank | undefined): SliderState {
  const s = copyPrimaries(floor);
  if (!lane) return s;
  if (lane.instinct != null && Number.isFinite(lane.instinct)) {
    s.instinct = clamp01_100(lane.instinct);
  }
  if (lane.bond != null && Number.isFinite(lane.bond)) {
    s.bond = clamp01_100(lane.bond);
  }
  if (lane.grit != null && Number.isFinite(lane.grit)) {
    s.grit = clamp01_100(lane.grit);
  }
  if (lane.insight != null && Number.isFinite(lane.insight)) {
    s.insight = clamp01_100(lane.insight);
  } else {
    const m = lane.measure;
    const sp = lane.selfProtect;
    if (
      (m != null && Number.isFinite(m)) ||
      (sp != null && Number.isFinite(sp))
    ) {
      const mv = m != null && Number.isFinite(m) ? m : s.insight;
      const sv = sp != null && Number.isFinite(sp) ? sp : s.insight;
      s.insight = clamp01_100(0.5 * mv + 0.5 * sv);
    }
  }
  return s;
}

function formatDerivedValue(v: number): string {
  return formatDisplay(v, 1);
}

const METRIC_INT_KEYS = new Set([
  "partyDeaths",
  "partyAlive",
  "partyHpRemaining",
  "surviveTriggers",
  "targetSwitches",
]);

function formatMetrics(m: SimReport["metrics"]): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(m)) {
    if (v == null) continue;
    const n =
      typeof v === "number"
        ? METRIC_INT_KEYS.has(k)
          ? formatInt(v)
          : formatDisplay(v, 1)
        : String(v);
    parts.push(`${k}=${n}`);
  }
  return parts.join(" · ");
}

export function behaviorLabel(activeId: string | null | undefined): string {
  if (!activeId) return "—";
  const gambitKey = `raid.gambit.beh.${activeId}`;
  if (hasString(gambitKey)) return t(gambitKey);
  const simKey = `raid.sim.beh.${activeId}`;
  if (hasString(simKey)) return t(simKey);
  return activeId;
}

function axisLabel(key: SliderKey): string {
  return judgmentSellHelp().traits[key as JudgmentTraitHelpId].nameKo;
}

/** Trait cores = player `raid_leader`; long planner prose → Hub Wiki. */
function simHelpBodyHtml(): string {
  const doc = judgmentSellHelp();
  const rows = SLIDER_KEYS.map((key) => {
    const id = key as JudgmentTraitHelpId;
    const tr = doc.traits[id];
    return `
      <div class="raid-sim-help-row${key === "grit" ? " is-demoted" : ""}">
        <strong class="raid-sim-help-lab">${escapeAttr(tr.nameKo)}</strong>
        <p class="raid-sim-help-blurb">${escapeAttr(raidLeaderCore(doc, id))}</p>
      </div>`;
  }).join("");
  return `
    <p class="raid-sim-help-intro">${escapeAttr(doc.raidLeader.introPlain)}</p>
    <p class="raid-sim-help-floor">Soft 바닥(비재능): thrallChew 생존 ×1.22·multi-rose는 파티 Soft 상수 — 직감/통찰/유대 슬라이더로 예측하지 말 것.</p>
    ${rows}
    <p class="raid-sim-help-wiki">${escapeAttr(t("raid.sim.help.wikiHint"))}</p>
  `;
}

type LaneApi = {
  setState: (s: SliderState) => void;
  setFocusPrimaries: (keys: readonly SimPrimaryId[]) => void;
  setEditablePrimaries: (keys: readonly SimPrimaryId[]) => void;
  setPlaybackLocked: (locked: boolean) => void;
  inputs: HTMLInputElement[];
};

function mountLane(
  host: HTMLElement,
  lane: "A" | "B",
  initial: SliderState,
  onChange: (next: SliderState) => void,
): LaneApi {
  host.replaceChildren();

  const head = document.createElement("div");
  head.className = "raid-sim-lane-head";
  const title = document.createElement("div");
  title.className = "raid-sim-lane-title";
  title.textContent = lane === "A" ? t("raid.sim.bankA") : t("raid.sim.bankB");
  head.appendChild(title);
  host.appendChild(head);

  let state = copyPrimaries(initial);
  let playbackLocked = false;
  const inputs: HTMLInputElement[] = [];
  const inputEls = new Map<SliderKey, HTMLInputElement>();
  const valEls = new Map<SliderKey, HTMLElement>();
  const derivedEls = new Map<SliderKey, HTMLElement>();
  const blockEls = new Map<SliderKey, HTMLElement>();

  for (const key of SLIDER_KEYS) {
    const block = document.createElement("div");
    block.className =
      key === "grit"
        ? "raid-sim-slider-block is-demoted"
        : "raid-sim-slider-block";
    block.dataset.axis = key;

    const row = document.createElement("label");
    row.className = "raid-sim-slider-row";
    const lab = document.createElement("span");
    lab.className = "raid-sim-slider-lab";
    lab.textContent = axisLabel(key);
    const input = document.createElement("input");
    input.type = "range";
    input.min = "0";
    input.max = "100";
    input.step = "1";
    input.value = String(state[key]);
    input.dataset.axis = key;
    input.dataset.lane = lane;
    const val = document.createElement("span");
    val.className = "raid-sim-slider-val";
    val.dataset.val = key;
    val.textContent = String(state[key]);
    row.appendChild(lab);
    row.appendChild(input);
    row.appendChild(val);

    const derived = document.createElement("div");
    derived.className = "raid-sim-derived";
    derived.dataset.derived = key;

    block.appendChild(row);
    block.appendChild(derived);
    host.appendChild(block);

    inputs.push(input);
    inputEls.set(key, input);
    valEls.set(key, val);
    derivedEls.set(key, derived);
    blockEls.set(key, block);
  }

  const paintDerived = (): void => {
    const insight = clamp01_100(state.insight);
    const preview = previewAiDerivedFromPrimaries({
      grit: state.grit,
      measure: insight,
      selfProtect: insight,
      bond: state.bond,
      instinct: state.instinct,
    });
    for (const key of SLIDER_KEYS) {
      const el = derivedEls.get(key);
      if (!el) continue;
      if (key === "insight") {
        const rows = [...preview.measure, ...preview.selfProtect];
        el.textContent = rows.length
          ? rows
              .map((r) => `${r.key}=${formatDerivedValue(r.value)}`)
              .join(" · ")
          : "—";
        continue;
      }
      const rows = preview[key];
      el.textContent = rows.length
        ? rows.map((r) => `${r.key}=${formatDerivedValue(r.value)}`).join(" · ")
        : "—";
    }
  };

  const applyInputDisabled = (): void => {
    for (const key of SLIDER_KEYS) {
      const input = inputEls.get(key);
      const block = blockEls.get(key);
      if (!input || !block) continue;
      block.classList.toggle("is-locked", false);
      input.disabled = playbackLocked;
    }
  };

  /** Paint all four primaries — never only focusPrimaries. */
  const applyState = (next: SliderState): void => {
    state = copyPrimaries(next);
    for (const key of SLIDER_KEYS) {
      const input = inputEls.get(key);
      const val = valEls.get(key);
      if (!input || !val) continue;
      const wasDisabled = input.disabled;
      if (wasDisabled) input.disabled = false;
      input.value = String(state[key]);
      input.setAttribute("value", String(state[key]));
      if (wasDisabled) input.disabled = true;
      val.textContent = String(state[key]);
    }
    applyInputDisabled();
    paintDerived();
    onChange(copyPrimaries(state));
  };

  host.addEventListener("input", (ev) => {
    const el = ev.target as HTMLInputElement;
    if (el.type !== "range") return;
    if (!el.dataset.axis || el.dataset.lane !== lane) return;
    const key = el.dataset.axis as SliderKey;
    if (!SLIDER_KEYS.includes(key)) return;
    const v = clamp01_100(Number(el.value));
    state = { ...state, [key]: v };
    valEls.get(key)!.textContent = String(v);
    paintDerived();
    onChange(copyPrimaries(state));
  });

  paintDerived();
  applyInputDisabled();

  return {
    inputs,
    setFocusPrimaries(keys: readonly SimPrimaryId[]) {
      const set = new Set<string>(keys);
      if (set.has("measure") || set.has("selfProtect")) set.add("insight");
      for (const key of SLIDER_KEYS) {
        blockEls.get(key)?.classList.toggle("is-focus", set.has(key));
      }
    },
    setEditablePrimaries(_keys: readonly SimPrimaryId[]) {
      applyInputDisabled();
    },
    setState(next: SliderState) {
      applyState(next);
    },
    setPlaybackLocked(next: boolean) {
      playbackLocked = next;
      applyInputDisabled();
    },
  };
}

/** Merge absolute bank over authored floor (missing keys keep floor). */
function bankFromLane(
  floor: SliderState,
  lane: SimLaneBank | undefined,
): SliderState {
  return bankToSlider(floor, lane);
}

export function mountRaidSimHud(handlers: RaidSimHudHandlers): RaidSimHudApi {
  const ui = getUiLayout();
  ui.sync();
  ui.clearShell();
  // Full viewport — side letterbox holds A/B banks; canvas shows through center.
  const host = ui.overlay;
  host.style.pointerEvents = "none";

  const scenarios = listSimScenarios();
  let scenarioId: SimScenarioId = scenarios[0]!.id;
  let focusDefId = scenarios[0]!.focusDefId;
  let bankA = floorFor(focusDefId);
  let bankB = floorFor(focusDefId);
  let playMode: SimPlayMode = "single";
  let transportPhase: SimTransportPhase = "ready";
  let cueQueued = false;

  const panel = document.createElement("div");
  panel.className = "raid-sim-panel";
  panel.innerHTML = `
    <div class="raid-sim-chrome">
      <div class="raid-sim-chrome-main">
        <h1 class="raid-sim-title" data-title></h1>
        <p class="raid-sim-hint" data-hint></p>
        <label class="raid-sim-field">
          <span data-lab-scenario></span>
          <select data-scenario></select>
        </label>
        <p class="raid-sim-scenario-desc" data-scenario-desc></p>
        <div class="raid-sim-mode" role="group" data-mode-group>
          <button type="button" class="menu-btn raid-sim-mode-btn is-active" data-mode="single"></button>
          <button type="button" class="menu-btn raid-sim-mode-btn" data-mode="compare"></button>
        </div>
        <details class="raid-sim-help" data-help>
          <summary class="raid-sim-help-sum" data-help-sum></summary>
          <div class="raid-sim-help-body" data-help-body></div>
        </details>
        <div class="raid-sim-transport" role="group" data-transport>
          <button type="button" class="menu-btn raid-sim-transport-btn" data-play></button>
          <button type="button" class="menu-btn raid-sim-transport-btn" data-pause disabled></button>
          <button type="button" class="menu-btn raid-sim-transport-btn" data-stop disabled></button>
          <button type="button" class="menu-btn raid-sim-transport-btn" data-reset></button>
        </div>
        <p class="raid-sim-transport-hint" data-transport-hint></p>
      </div>
    </div>
    <aside class="raid-sim-lane raid-sim-lane-a" data-lane-a></aside>
    <div class="raid-sim-stage-gap" aria-hidden="true"></div>
    <aside class="raid-sim-lane raid-sim-lane-b" data-lane-b></aside>
    <div class="raid-sim-footer">
      <p class="raid-sim-status" data-status></p>
      <p class="raid-sim-caption" data-caption></p>
      <pre class="raid-sim-out" data-out></pre>
      <div class="raid-sim-nav">
        <button type="button" class="menu-btn" data-roster></button>
        <button type="button" class="menu-btn" data-back></button>
      </div>
    </div>
  `;
  host.appendChild(panel);

  panel.querySelector("[data-title]")!.textContent = t("raid.sim.title");
  panel.querySelector("[data-hint]")!.textContent = t("raid.sim.hint");
  panel.querySelector("[data-lab-scenario]")!.textContent = t(
    "raid.sim.scenario",
  );
  panel.querySelector("[data-help-sum]")!.textContent = t("raid.sim.help.title", {
    title: judgmentSellHelp().title,
  });
  panel.querySelector("[data-help-body]")!.innerHTML = simHelpBodyHtml();
  panel.querySelector("[data-transport-hint]")!.textContent = t(
    "raid.sim.transportHint",
  );

  const sel = panel.querySelector("[data-scenario]") as HTMLSelectElement;
  for (const s of scenarios) {
    const opt = document.createElement("option");
    opt.value = s.id;
    const tierTag =
      s.tier === "multi" ? t("raid.sim.tier.multi") : t("raid.sim.tier.single");
    opt.textContent = `${tierTag} ${t(s.labelKey)}`;
    sel.appendChild(opt);
  }
  sel.value = scenarioId;

  const modeSingleBtn = panel.querySelector(
    '[data-mode="single"]',
  ) as HTMLButtonElement;
  const modeCompareBtn = panel.querySelector(
    '[data-mode="compare"]',
  ) as HTMLButtonElement;
  modeSingleBtn.textContent = t("raid.sim.mode.single");
  modeCompareBtn.textContent = t("raid.sim.mode.compare");

  const laneAEl = panel.querySelector("[data-lane-a]") as HTMLElement;
  const laneBEl = panel.querySelector("[data-lane-b]") as HTMLElement;

  const laneA = mountLane(laneAEl, "A", bankA, (s) => {
    bankA = s;
  });
  const laneB = mountLane(laneBEl, "B", bankB, (s) => {
    bankB = s;
  });

  const statusEl = panel.querySelector("[data-status]") as HTMLElement;
  const captionEl = panel.querySelector("[data-caption]") as HTMLElement;
  const out = panel.querySelector("[data-out]") as HTMLElement;
  const scenarioDescEl = panel.querySelector(
    "[data-scenario-desc]",
  ) as HTMLElement;
  statusEl.textContent = t("raid.sim.ready");

  const playBtn = panel.querySelector("[data-play]") as HTMLButtonElement;
  const pauseBtn = panel.querySelector("[data-pause]") as HTMLButtonElement;
  const stopBtn = panel.querySelector("[data-stop]") as HTMLButtonElement;
  const resetBtn = panel.querySelector("[data-reset]") as HTMLButtonElement;

  playBtn.textContent = t("raid.sim.play");
  playBtn.title = t("raid.sim.playHint");
  pauseBtn.textContent = t("raid.sim.pause");
  pauseBtn.title = t("raid.sim.pauseHint");
  stopBtn.textContent = t("raid.sim.stop");
  stopBtn.title = t("raid.sim.stopHint");
  resetBtn.textContent = t("raid.sim.reset");
  resetBtn.title = t("raid.sim.resetHint");

  /**
   * Authored floor for non-focus axes (role flavor), but focus primaries
   * start at neutral 50 — otherwise tank authored floor outside the mid band
   * looks "dead" at mid when the slider defaulted to floor.
   * Pitch scenarios replace both banks with authored pitchBanks A|B.
   */
  const bankForScenario = (
    row: (typeof scenarios)[number],
    floor: SliderState,
    lane: "A" | "B",
  ): SliderState => {
    const pitch = row.pitchBanks?.[lane];
    if (pitch) return bankFromLane(floor, pitch);
    const s = copyPrimaries(floor);
    for (const key of row.focusPrimaries) {
      if (key === "insight" || key === "measure" || key === "selfProtect") {
        s.insight = NEUTRAL_PRIMARIES.insight;
      } else if (key === "instinct" || key === "bond" || key === "grit") {
        s[key] = NEUTRAL_PRIMARIES[key];
      }
    }
    return s;
  };

  const applyScenarioUi = (): void => {
    const row = scenarios.find((x) => x.id === scenarioId)!;
    focusDefId = row.focusDefId;
    scenarioDescEl.textContent = t(row.descKey);
    const focus = row.focusPrimaries;
    laneA.setFocusPrimaries(focus);
    laneB.setFocusPrimaries(focus);
    laneA.setEditablePrimaries(focus);
    laneB.setEditablePrimaries(focus);
  };

  /** Load scenario pitch/floor initials into A|B banks. */
  const syncFocus = (): void => {
    const row = scenarios.find((x) => x.id === scenarioId)!;
    const floor = floorFor(focusDefId);
    bankA = bankForScenario(row, floor, "A");
    bankB = bankForScenario(row, floor, "B");
    laneA.setState(bankA);
    laneB.setState(bankB);
  };

  const buildCue = (): SimCueRequest => ({
    mode: playMode,
    scenarioId,
    a: { scenarioId, patch: patchFromAbsolute(focusDefId, bankA) },
    b: { scenarioId, patch: patchFromAbsolute(focusDefId, bankB) },
  });

  const paintMode = (): void => {
    modeSingleBtn.classList.toggle("is-active", playMode === "single");
    modeCompareBtn.classList.toggle("is-active", playMode === "compare");
  };

  const paintTransport = (): void => {
    const btns = simTransportButtons(transportPhase);
    playBtn.disabled = !btns.play;
    pauseBtn.disabled = !btns.pause;
    stopBtn.disabled = !btns.stop;
    resetBtn.disabled = !btns.reset;
    sel.disabled = !btns.edit;
    modeSingleBtn.disabled = !btns.edit;
    modeCompareBtn.disabled = !btns.edit;
    // Mid-run pause keeps banks locked so resume stays honest.
    const banksLocked = !btns.edit;
    laneA.setPlaybackLocked(banksLocked);
    laneB.setPlaybackLocked(banksLocked);
  };

  const queueCue = (): void => {
    if (cueQueued) return;
    cueQueued = true;
    queueMicrotask(() => {
      cueQueued = false;
      handlers.onCue(buildCue());
    });
  };

  applyScenarioUi();
  syncFocus();
  paintMode();
  paintTransport();
  queueCue();

  sel.addEventListener("change", () => {
    if (sel.disabled) return;
    scenarioId = sel.value as SimScenarioId;
    out.textContent = "";
    captionEl.textContent = "";
    statusEl.textContent = t("raid.sim.ready");
    applyScenarioUi();
    syncFocus();
    queueCue();
  });

  const setMode = (next: SimPlayMode): void => {
    if (modeSingleBtn.disabled) return;
    if (playMode === next) return;
    playMode = next;
    paintMode();
    out.textContent = "";
    queueCue();
  };
  modeSingleBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    setMode("single");
  });
  modeCompareBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    setMode("compare");
  });

  playBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (playBtn.disabled) return;
    out.textContent = "";
    handlers.onPlay(buildCue());
  });
  pauseBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (pauseBtn.disabled) return;
    handlers.onPause();
  });
  stopBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (stopBtn.disabled) return;
    out.textContent = "";
    handlers.onStop(buildCue());
  });
  resetBtn.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (resetBtn.disabled) return;
    out.textContent = "";
    captionEl.textContent = "";
    applyScenarioUi();
    syncFocus();
    handlers.onReset(buildCue());
  });

  const rosterBtn = panel.querySelector("[data-roster]") as HTMLButtonElement;
  const backBtn = panel.querySelector("[data-back]") as HTMLButtonElement;
  rosterBtn.textContent = t("raid.sim.toRoster");
  backBtn.textContent = t("raid.sim.back");
  rosterBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onToRoster();
  });
  backBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onBack();
  });

  return {
    destroy() {
      panel.remove();
      ui.clearShell();
    },
    setTransportPhase(phase: SimTransportPhase) {
      transportPhase = phase;
      paintTransport();
    },
    setStatus(status: RaidSimHudStatus) {
      if (status.phase === "idle" || status.phase === "ready") {
        statusEl.textContent = t("raid.sim.ready");
      } else if (status.phase === "playing") {
        const tag = status.tag ? ` [${status.tag}]` : "";
        const beh = behaviorLabel(status.activeId);
        const sec =
          status.elapsedSec != null
            ? formatDisplay(status.elapsedSec, 1)
            : "0";
        statusEl.textContent = `${t("raid.sim.playing")}${tag} · ${beh} · ${sec}s`;
      } else if (status.phase === "paused") {
        const tag = status.tag ? ` [${status.tag}]` : "";
        const sec =
          status.elapsedSec != null
            ? ` · ${formatDisplay(status.elapsedSec, 1)}s`
            : "";
        statusEl.textContent = `${t("raid.sim.paused")}${tag}${sec}`;
      } else {
        const tag = status.tag ? ` [${status.tag}]` : "";
        statusEl.textContent = `${t("raid.sim.done")}${tag}`;
      }
      captionEl.textContent = status.caption ?? "";
    },
    setOutcome({ reports, summary }) {
      const lines: string[] = [];
      if (reports.length === 1) {
        lines.push(t("raid.sim.result"), formatMetrics(reports[0]!.metrics));
      } else if (reports.length >= 2) {
        lines.push(
          t("raid.sim.compareResult"),
          `A: ${formatMetrics(reports[0]!.metrics)}`,
          `B: ${formatMetrics(reports[1]!.metrics)}`,
        );
        if (summary?.length) lines.push(...summary);
      }
      out.textContent = lines.join("\n");
    },
  };
}
